import { IconNames } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import { useCallback, useEffect, useState } from 'react';
import { useInterval } from 'react-use';

import { authApi, OAuthProviderConnectionStatus } from 'apis/auth';

/** Describes the status icon. */
type OAuthConnectionStatusIcon = {
  icon: IconNames;
  color: string;
  tooltip: string;
};

/** Represents the provider connection status mapped to icon properties. */
type OAuthConnectionStatus = Record<
  OAuthProviderConnectionStatus,
  OAuthConnectionStatusIcon
>;

/** The status types. */
const statusTypes: OAuthConnectionStatus = {
  CONNECTED: {
    icon: 'check-circle-filled',
    color: colors.green[500],
    tooltip: 'Connected',
  },
  CONNECTING: {
    icon: 'sync',
    color: colors.neutral[500],
    tooltip: 'Connecting..',
  },
  CONNECTION_FAILED: {
    icon: 'close-circle-filled',
    color: colors.red[500],
    tooltip: 'Connection failed',
  },
  CONNECTION_TIMEOUT: {
    icon: 'exclamation-triangle-filled',
    color: colors.red[200],
    tooltip: 'Connection timeout',
  },
  NOT_CONNECTED: {
    icon: 'exclamation-circle-filled',
    color: colors.red[500],
    tooltip: 'Not connected',
  },
};

/** The default status type. */
const defaultStatusType: OAuthConnectionStatusIcon = {
  icon: 'exclamation-circle-filled',
  color: colors.neutral[500],
  tooltip: 'Not connected',
};

/** Returns the properties of a status icon based on the status type. */
const getConnectionStatusIcon = (
  type?: OAuthProviderConnectionStatus,
): OAuthConnectionStatusIcon => {
  if (type && type in statusTypes) {
    return statusTypes[type];
  }

  return defaultStatusType;
};

/** Represents the response of the hook. */
type UseOAuthAuthorizationResponse = {
  connectionStatus: OAuthProviderConnectionStatus;
  connectionStatusIcon: OAuthConnectionStatusIcon;
  handleAuthorization: () => Promise<void>;
  resetConnectionStatus: () => void;
  checkConnectionStatus: () => Promise<void>;
};

/**
 * A hook that handles the OAuth consent authorization process.
 *
 * @param providerId - The ID of the OAuth provider.
 * @returns The authorization state and methods.
 */
export const useOAuthAuthorization = (
  providerId?: string,
): UseOAuthAuthorizationResponse => {
  const maxPolls = 30;
  const pollDelay = 1000; // every second

  const [connectionStatus, setConnectionStatus] =
    useState<OAuthProviderConnectionStatus>('NOT_CONNECTED');

  const [pollCount, setPollCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const resetPolling = () => {
    setIsPolling(false);
    setPollCount(0);
  };

  const resetConnectionStatus = () => {
    setConnectionStatus('NOT_CONNECTED');
    resetPolling();
  };

  const [getOAuthConnection] =
    authApi.useLazyReadOAuthProviderConnectionByIdQuery();

  const [getOAuthConnectionStatus] =
    authApi.useLazyReadOAuthProviderConnectionStatusByIdQuery();

  /** The interval hook that handles the polling. */
  useInterval(
    () => {
      if (pollCount >= maxPolls) {
        setConnectionStatus('CONNECTION_TIMEOUT');
        resetPolling();
      } else {
        checkConnectionStatus();
        setPollCount(pollCount + 1);
      }
    },
    isPolling ? pollDelay : null,
  );

  /** Checks the status of the OAuth provider connection. */
  const checkConnectionStatus = useCallback(async () => {
    if (!providerId) return;

    try {
      const response = await getOAuthConnectionStatus({
        id: providerId,
      }).unwrap();

      if (isResponseWithData(response)) {
        const status = response.data.status;

        setConnectionStatus(status);

        if (status === 'CONNECTED' || status === 'NOT_CONNECTED') {
          resetPolling();
        }
      } else {
        setConnectionStatus('CONNECTION_FAILED');
        resetPolling();
      }
    } catch (error) {
      setConnectionStatus('CONNECTION_FAILED');
      resetPolling();
    }
  }, [providerId, getOAuthConnectionStatus]);

  useEffect(() => {
    // Check connection status when providerId is available
    if (providerId) {
      checkConnectionStatus();
    }
  }, [providerId, checkConnectionStatus]);

  /**
   * Handles the authorization process. Opens a popup window for OAuth consent
   * flow.
   */
  const handleAuthorization = async () => {
    if (!providerId) return;

    try {
      const response = await getOAuthConnection({
        id: providerId,
      }).unwrap();

      if (isResponseWithData(response)) {
        const url = response.data.url;
        const windowWidth = 620;
        const windowHeight = 800;
        // centering the new window
        const left = window.innerWidth / 2 - windowWidth / 2;
        const top = window.innerHeight / 2 - windowHeight / 2;

        setConnectionStatus('CONNECTING');
        setIsPolling(true);

        window.open(
          url,
          'DixaOAuthConsent',
          `toolbar=no,
            location=no,
            status=no,
            menubar=no,
            scrollbars=yes,
            resizable=yes,
            width=${windowWidth},
            height=${windowHeight},
            left=${left},
            top=${top}`,
        );
      }
    } catch (error) {
      setConnectionStatus('CONNECTION_FAILED');
      resetPolling();
    }
  };

  return {
    connectionStatus,
    connectionStatusIcon: getConnectionStatusIcon(connectionStatus),
    handleAuthorization,
    resetConnectionStatus,
    checkConnectionStatus,
  };
};
